import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { packageService } from '@/api/services/packageService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { cn } from '@/shared/utils'
import { X } from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold text-gray-700 block mb-1.5">
      {children}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  )
}
function Input({ ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input {...props}
      className={cn('w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white placeholder-gray-300', props.className)}/>
  )
}
function Textarea({ ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea {...props}
      className={cn('w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white placeholder-gray-300', props.className)}/>
  )
}
function Select({ children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode }) {
  return (
    <select {...props}
      className={cn('w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white', props.className)}>
      {children}
    </select>
  )
}
function SectionHeader({ title }: { title: string }) {
  return (
    <div className="bg-gray-100 rounded-xl px-4 py-2.5 mb-4">
      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{title}</span>
    </div>
  )
}

// ── Package color options ─────────────────────────────────────────────────────
const PACKAGE_COLORS = [
  { value:'unlimited', label:'Unlimited', gradient:'linear-gradient(to right, #7B156D, #3B0033)' },
  { value:'gold',      label:'Gold',      gradient:'linear-gradient(to right, #FFB000, #000000)' },
  { value:'silver',    label:'Silver',    gradient:'linear-gradient(to right, #8E8E8E, #000000)' },
  { value:'bronze',    label:'Bronze',    gradient:'linear-gradient(to right, #CD7F32, #000000)' },
  { value:'express',   label:'Express',   gradient:'linear-gradient(to right, #DC2626, #000000)' },
]

// ══════════════════════════════════════════════════════════════════════════════
// Create Package Page
// ══════════════════════════════════════════════════════════════════════════════
export function PackageCreatePage({ editPackage }: { editPackage?: any }) {
  const { t }     = useTranslation()
  const confirm   = useConfirm()
  const navigate  = useNavigate()
  const qc        = useQueryClient()
  const isEdit    = !!editPackage

  // ── Form state ─────────────────────────────────────────────────────────────
  const cap = (s?: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

  const [nameEn,    setNameEn]    = useState(editPackage?.package_translations?.find((t:any)=>t.locale?.toLowerCase()==='en')?.name ?? '')
  const [nameAr,    setNameAr]    = useState(editPackage?.package_translations?.find((t:any)=>t.locale?.toLowerCase()==='ar')?.name ?? '')
  const [nameUr,    setNameUr]    = useState(editPackage?.package_translations?.find((t:any)=>t.locale?.toLowerCase()==='ur')?.name ?? '')
  // Package inclusions per locale — sent as translations[].session_inclusions (matches the old dashboard)
  const inclOf = (loc: string) =>
    editPackage?.package_translations?.find((t:any)=>t.locale?.toLowerCase()===loc)?.session_inclusions ?? ''
  const [inclEn,    setInclEn]    = useState(inclOf('en'))
  const [inclAr,    setInclAr]    = useState(inclOf('ar'))
  const [inclUr,    setInclUr]    = useState(inclOf('ur'))
  const [category,  setCategory]  = useState<any>(editPackage?.category ?? (editPackage?.category_id ? { id: editPackage.category_id } : null))
  const [cityIds,   setCityIds]   = useState<number[]>(() =>
    (editPackage?.package_city ?? []).map((pc:any)=>pc.city_id).filter((x:any)=>x != null))
  const [schoolIds, setSchoolIds] = useState<number[]>(() => {
    if (Array.isArray(editPackage?.package_vendor)) return editPackage.package_vendor.map((v:any)=>v.vendor_id ?? v.vendor?.id).filter(Boolean)
    return editPackage?.vendor_id ? [editPackage.vendor_id] : []
  })
  const [color,     setColor]     = useState(editPackage?.background_color ?? 'unlimited')
  // City pricing keyed by the cities selected in the City multi-select above
  type CityPrice = { packageCityId?: number; price: string; commision: string; discountType: string; discountValue: string; startDate: string; endDate: string }
  const emptyCityPrice = (): CityPrice => ({ price:'', commision:'', discountType:'Percentage', discountValue:'', startDate:'', endDate:'' })
  const [cityPricing, setCityPricing] = useState<Record<number, CityPrice>>(() => {
    const out: Record<number, CityPrice> = {}
    ;(editPackage?.package_city ?? []).forEach((pc:any) => {
      if (pc.city_id == null) return
      out[pc.city_id] = {
        // The package↔city mapping id (used on edit so the backend updates in place).
        packageCityId: pc.id ?? pc.package_city_id,
        price:         pc.min_price != null ? String(pc.min_price) : '',
        commision:     pc.commision != null ? String(pc.commision) : '',
        discountType:  pc.discount_type ? cap(pc.discount_type) : 'Percentage',
        discountValue: pc.discount_value != null ? String(pc.discount_value) : '',
        startDate:     (pc.offer_valid_from ?? '').slice(0, 10),
        endDate:       (pc.offer_valid_until ?? '').slice(0, 10),
      }
    })
    return out
  })
  const setCityPrice = (id: number, k: keyof CityPrice, v: string) =>
    setCityPricing(p => ({ ...p, [id]: { ...(p[id] ?? emptyCityPrice()), [k]: v } }))
  // Commission
  const [hasDrivysComm,  setHasDrivysComm]  = useState(Number(editPackage?.drivys_commision ?? 0) > 0)
  const [commType,       setCommType]       = useState(editPackage?.is_drivys_commision_percentage ? 'Percentage' : (isEdit ? 'Flat Amount' : 'Percentage'))
  const [commValue,      setCommValue]      = useState(editPackage?.drivys_commision != null ? String(editPackage.drivys_commision) : '')
  // Session
  const [sessionType,    setSessionType]    = useState(editPackage?.is_unlimited || editPackage?.number_of_sessions === -1 ? 'Unlimited' : 'Limit')
  const [sessionCount,   setSessionCount]   = useState(editPackage?.number_of_sessions && editPackage.number_of_sessions !== -1 ? String(editPackage.number_of_sessions) : '8')
  // Session titles (pairs)
  const sessionSlots = useMemo(() => {
    const n = sessionType === 'Unlimited' ? 0 : Math.floor(Number(sessionCount || 0) / 2)
    return Array.from({ length: n }, (_, i) => i)
  }, [sessionType, sessionCount])
  const [sessionTitles, setSessionTitles] = useState<Record<number, {en:string;ar:string;ur:string}>>(() => {
    const out: Record<number, {en:string;ar:string;ur:string}> = {}
    ;(editPackage?.session_details ?? []).forEach((sd:any) => {
      const idx = (sd.slot_number ?? 0) - 1
      if (idx < 0) return
      const pick = (loc:string) => sd.translations?.find((t:any)=>t.locale?.toLowerCase()===loc)?.title
        ?? sd.translations?.find((t:any)=>t.locale?.toLowerCase()===loc)?.name ?? ''
      out[idx] = { en: pick('en'), ar: pick('ar'), ur: pick('ur') }
    })
    return out
  })
  // Settings
  const [publish,      setPublish]      = useState(editPackage?.is_published === 1 || editPackage?.is_published === true)
  const [pickup,       setPickup]       = useState(!!editPackage?.is_pickup_fee_included)
  const [freePickup,   setFreePickup]   = useState(editPackage?.free_pickup_count != null ? String(editPackage.free_pickup_count) : '')
  const [certificate,  setCertificate]  = useState(!!editPackage?.is_certificate_included)
  const [cashPay,      setCashPay]      = useState(!!editPackage?.is_cash_pay_available)
  const [trainerChange,setTrainerChange]= useState(editPackage?.trainer_change_policy_enabled === 1 || !!editPackage?.is_change_trainer_allowed)
  const [freeTrainerChange, setFreeTrainerChange] = useState(editPackage?.free_trainer_change_count != null ? String(editPackage.free_trainer_change_count) : '')
  const [reschedule,   setReschedule]   = useState(editPackage?.reschedule_policy_enabled === 1 || !!editPackage?.is_reschedule_allowed)
  const [freeReschedule, setFreeReschedule] = useState(editPackage?.free_reschedule_count != null ? String(editPackage.free_reschedule_count) : '')
  const [rescheduleMin,  setRescheduleMin]  = useState(editPackage?.reschedule_hours_before != null ? String(Math.round(Number(editPackage.reschedule_hours_before))) : '30')
  const [freeKm,       setFreeKm]       = useState(editPackage?.free_km != null ? String(editPackage.free_km) : '')
  const [loading,      setLoading]      = useState(false)
  const [error,        setError]        = useState('')

  // ── Data queries ───────────────────────────────────────────────────────────
  const { data: catData }    = useQuery({ queryKey:['categories-pkg'], queryFn:()=>restClient.get(`${endpoints.users.listCategory}?page=1&limit=1000&published=1`).then(r=>r.data), staleTime: Infinity })
  const { data: cityData }   = useQuery({ queryKey:['cities-pkg'],     queryFn:()=>restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r=>r.data), staleTime: Infinity })
  const { data: schoolData } = useQuery({ queryKey:['schools-pkg'],    queryFn:()=>restClient.get(`${endpoints.school.list}?page=1&limit=1000`).then(r=>r.data), staleTime: Infinity })

  const categories: any[] = catData?.data  ?? []
  const cities: any[]     = cityData?.data  ?? []
  const schools: any[]    = schoolData?.data ?? []

  const getSchoolName = (s: any) => s.vendor_translations?.find((t:any)=>t.locale?.toLowerCase()==='en')?.name ?? s.vendor_translations?.[0]?.name ?? 'Unknown'
  const getCategoryName = (c: any) => c.category_translations?.find((t:any)=>t.locale?.toLowerCase()==='en')?.name ?? c.category_translations?.[0]?.name ?? 'Unknown'
  const getCityName = (c: any) => c.city_translations?.find((t:any)=>t.locale==='En')?.name ?? c.city_translations?.[0]?.name ?? 'Unknown'

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!nameEn.trim()) { setError('Package Name (En) is required'); return }
    if (cityIds.length === 0) { setError('Please select at least one City'); return }
    if (schoolIds.length === 0) { setError('Please select at least one School'); return }
    if (isEdit && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    setError(''); setLoading(true)
    try {
      // Mirrors the old dashboard: emit a locale entry when either the name or the
      // inclusions text is filled, and always carry session_inclusions.
      const mkTranslation = (locale: string, name: string, incl: string) =>
        (name.trim() || incl.trim())
          ? [{ locale, name: name.trim(), session_inclusions: incl.trim() }]
          : []
      const translations = [
        ...mkTranslation('En', nameEn, inclEn),
        ...mkTranslation('Ar', nameAr, inclAr),
        ...mkTranslation('Ur', nameUr, inclUr),
      ]
      // session_titles must be grouped by locale, each with a `titles` array
      // (NOT per-slot objects). Locale codes are lowercase — matches the backend
      // and the old dashboard's payload.
      const mkSlotTitles = (loc: 'en' | 'ar' | 'ur') => {
        const titles = sessionSlots
          .map(i => (sessionTitles[i]?.[loc] ?? '').trim())
          .filter(Boolean)
        return titles.length ? [{ locale: loc, titles }] : []
      }
      const sessionTitlesArr = [
        ...mkSlotTitles('en'),
        ...mkSlotTitles('ar'),
        ...mkSlotTitles('ur'),
      ]
      const citiesArr = cityIds.map(id => {
        const r = cityPricing[id] ?? emptyCityPrice()
        return {
          city_id:        id,
          // On edit, carry the existing package↔city mapping id so the backend
          // updates the row instead of creating a duplicate.
          ...(r.packageCityId ? { package_city_id: r.packageCityId } : {}),
          min_price:      Number(r.price || 0),
          max_price:      Number(r.price || 0),
          // commision / commision_type match the old dashboard's payload. Old derives
          // commision_type from the discount type: 'percentage' else 'amount'.
          commision:      Number(r.commision || 0),
          commision_type: r.discountType.toLowerCase() === 'percentage' ? 'percentage' : 'amount',
          discount_type:  r.discountType.toLowerCase(),
          discount_value: Number(r.discountValue || 0),
          offer_valid_from:  r.startDate || undefined,
          offer_valid_until: r.endDate || undefined,
        }
      })
      const payload: any = {
        ...(isEdit ? { package_id: editPackage.id } : {}),
        translations,
        category_id:    category?.id ?? null,
        vendor_id:      schoolIds,
        background_color: color,
        number_of_sessions: sessionType === 'Unlimited' ? -1 : Number(sessionCount),
        is_published:   publish ? 1 : 0,
        is_pickup_fee_included: pickup ? 1 : 0,
        free_pickup_count: pickup ? Number(freePickup||0) : 0,
        is_certificate_included: certificate ? 1 : 0,
        is_cash_pay_available: cashPay ? 1 : 0,
        is_change_trainer_allowed: trainerChange ? 1 : 0,
        free_km: Number(freeKm||0),
        drivys_commision: hasDrivysComm ? Number(commValue||0) : 0,
        is_drivys_commision_percentage: commType === 'Percentage' ? 1 : 0,
        cities: citiesArr,
        session_titles: sessionTitlesArr,
        // Reschedule / trainer-change settings go ONLY as nested policy objects —
        // this is the exact shape the backend expects (no flat is_reschedule_allowed
        // / free_* fields).
        reschedule_policy: reschedule
          ? { enabled: true,  free_reschedule_count: Number(freeReschedule || 0), reschedule_hours_before: Number(rescheduleMin || 30) }
          : { enabled: false, free_reschedule_count: 0, reschedule_hours_before: 30 },
        trainer_change_policy: trainerChange
          ? { enabled: true,  free_trainer_change_count: Number(freeTrainerChange || 0) }
          : { enabled: false, free_trainer_change_count: 0 },
      }
      await packageService.createUpdate(payload)
      qc.invalidateQueries({ queryKey: ['packages'] })
      navigate('/dashboard/package/list')
    } catch(e: any) {
      const msg = e?.errors ? Object.values(e.errors).flat().join(', ') : e?.message ?? 'Failed to save'
      setError(msg)
    } finally { setLoading(false) }
  }

  // ── Toggle helper ───────────────────────────────────────────────────────────
  const Toggle = ({ checked, onChange, id }: { checked: boolean; onChange: ()=>void; id: string }) => (
    <button type="button" onClick={onChange}
      className={cn('relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0', checked ? 'bg-brand-500' : 'bg-gray-200')}>
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0')}/>
    </button>
  )

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span className="cursor-pointer hover:text-brand-500" onClick={()=>navigate('/dashboard/package/list')}>Dashboard</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-brand-500" onClick={()=>navigate('/dashboard/package/list')}>Package Management</span>
            <span>/</span>
            <span className="text-brand-500 font-semibold">{isEdit ? 'Edit Package' : 'Create Package'}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Package' : 'Create Package'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>navigate('/dashboard/package/list')}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="px-6 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-50">
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 font-semibold">{error}</div>
      )}

      {/* ── Basic Info ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Basic Info"/>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <Label required>Package Name (En)</Label>
            <Input value={nameEn} onChange={e=>setNameEn(e.target.value)} placeholder="Enter Package Name"/>
          </div>
          <div>
            <Label>Package Name (Ar)</Label>
            <Input value={nameAr} onChange={e=>setNameAr(e.target.value)} placeholder="Enter Package Name" dir="rtl"/>
          </div>
          <div>
            <Label>Package Name (Ur)</Label>
            <Input value={nameUr} onChange={e=>setNameUr(e.target.value)} placeholder="Enter Package Name" dir="rtl"/>
          </div>
        </div>
        {/* Package Inclusions — one per locale, sent as translations[].session_inclusions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <Label>Package Inclusions (En)</Label>
            <Textarea value={inclEn} onChange={e=>setInclEn(e.target.value)} rows={2} placeholder="What's included in this package"/>
          </div>
          <div>
            <Label>Package Inclusions (Ar)</Label>
            <Textarea value={inclAr} onChange={e=>setInclAr(e.target.value)} rows={2} placeholder="What's included in this package" dir="rtl"/>
          </div>
          <div>
            <Label>Package Inclusions (Ur)</Label>
            <Textarea value={inclUr} onChange={e=>setInclUr(e.target.value)} rows={2} placeholder="What's included in this package" dir="rtl"/>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Vehicle Category */}
          <div>
            <Label required>Vehicle Category</Label>
            <Select value={category?.id ?? ''} onChange={e=>{const c=categories.find((x:any)=>String(x.id)===e.target.value); setCategory(c??null)}}>
              <option value="">Select Vehicle Category</option>
              {categories.map((c:any)=><option key={c.id} value={c.id}>{getCategoryName(c)}</option>)}
            </Select>
          </div>
          {/* City multi-select chips */}
          <div>
            <Label required>City</Label>
            <Select value="" onChange={e=>{const id=Number(e.target.value); if(!cityIds.includes(id)) setCityIds(prev=>[...prev,id])}}>
              <option value="">Select City</option>
              {cities.filter((c:any)=>!cityIds.includes(c.id)).map((c:any)=><option key={c.id} value={c.id}>{getCityName(c)}</option>)}
            </Select>
            {cityIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {cityIds.map(id=>{
                  const c=cities.find((x:any)=>x.id===id)
                  return c ? (
                    <span key={id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold">
                      {getCityName(c)}
                      <button onClick={()=>setCityIds(prev=>prev.filter(x=>x!==id))}><X size={10}/></button>
                    </span>
                  ) : null
                })}
              </div>
            )}
          </div>
          {/* School multi-select chips */}
          <div>
            <Label required>School</Label>
            <Select value="" onChange={e=>{const id=Number(e.target.value); if(!schoolIds.includes(id)) setSchoolIds(prev=>[...prev,id])}}>
              <option value="">Select School</option>
              {schools.filter((s:any)=>!schoolIds.includes(s.id)).map((s:any)=><option key={s.id} value={s.id}>{getSchoolName(s)}</option>)}
            </Select>
            {schoolIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {schoolIds.map(id=>{
                  const s=schools.find((x:any)=>x.id===id)
                  return s ? (
                    <span key={id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold">
                      {getSchoolName(s)}
                      <button onClick={()=>setSchoolIds(prev=>prev.filter(x=>x!==id))}><X size={10}/></button>
                    </span>
                  ) : null
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Package Color ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Package Color"/>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {PACKAGE_COLORS.map(c=>(
            <button key={c.value} onClick={()=>setColor(c.value)}
              style={{ background: c.gradient }}
              className={cn('py-3 rounded-xl text-white text-sm font-bold transition-all',
                color === c.value ? 'ring-2 ring-offset-2 ring-brand-500 scale-105' : 'opacity-80 hover:opacity-100')}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Per-city pricing (driven by selected cities) ── */}
      {cityIds.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <SectionHeader title="City Pricing"/>
          <p className="text-xs text-gray-400 text-center py-6">Select one or more cities above to set their pricing.</p>
        </div>
      ) : cityIds.map(id => {
        const c = cities.find((x:any)=>x.id===id)
        const r = cityPricing[id] ?? emptyCityPrice()
        return (
          <div key={id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <SectionHeader title={c ? getCityName(c) : `City #${id}`}/>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label required>Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">AED</span>
                  <Input value={r.price} onChange={e=>setCityPrice(id,'price',e.target.value)} type="number" min="0" placeholder="0.00" className="pl-10"/>
                </div>
              </div>
              <div>
                <Label>Commission</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{r.discountType==='Percentage'?'%':'AED'}</span>
                  <Input value={r.commision} onChange={e=>setCityPrice(id,'commision',e.target.value)} type="number" min="0" placeholder="0.00" className="pl-10"/>
                </div>
              </div>
              <div>
                <Label>Discount Type</Label>
                <Select value={r.discountType} onChange={e=>setCityPrice(id,'discountType',e.target.value)}>
                  <option>Percentage</option>
                  <option>Amount</option>
                </Select>
              </div>
              <div>
                <Label>Discount Value</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{r.discountType==='Percentage'?'%':'AED'}</span>
                  <Input value={r.discountValue} onChange={e=>setCityPrice(id,'discountValue',e.target.value)} type="number" min="0" placeholder="0.00" className="pl-10"/>
                </div>
              </div>
              <div>
                <Label required>Start Date</Label>
                <Input type="date" value={r.startDate} onChange={e=>setCityPrice(id,'startDate',e.target.value)}/>
              </div>
              <div>
                <Label required>End Date</Label>
                <Input type="date" value={r.endDate} onChange={e=>setCityPrice(id,'endDate',e.target.value)}/>
              </div>
            </div>
          </div>
        )
      })}

      {/* ── Commission Settings ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Commission Settings"/>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="flex items-center gap-3">
            <Toggle checked={hasDrivysComm} onChange={()=>setHasDrivysComm(v=>!v)} id="comm"/>
            <span className="text-sm text-gray-700 font-medium">Is there a Drivys commission?</span>
          </div>
          {hasDrivysComm && (
            <>
              <div>
                <Label>Commission Type</Label>
                <Select value={commType} onChange={e=>setCommType(e.target.value)}>
                  <option>Percentage</option>
                  <option>Flat Amount</option>
                </Select>
              </div>
              <div>
                <Label>Drivys Commission</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {commType==='Percentage'?'%':'AED'}
                  </span>
                  <Input value={commValue} onChange={e=>setCommValue(e.target.value)} type="number" min="0" placeholder="0.00" className="pl-10"/>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Session Info ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Session Info"/>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div>
            <Label required>Session</Label>
            <Select value={sessionType} onChange={e=>setSessionType(e.target.value)}>
              <option value="Limit">Limit</option>
              <option value="Unlimited">Unlimited</option>
            </Select>
          </div>
          {sessionType !== 'Unlimited' && (
            <div>
              <Label required>Number of Session</Label>
              <Select value={sessionCount} onChange={e=>setSessionCount(e.target.value)}>
                {[2,4,6,8,10,12,14,16,18,20].map(n=><option key={n} value={n}>{n}</option>)}
              </Select>
            </div>
          )}
        </div>

        {/* Session Titles */}
        {sessionSlots.length > 0 && (
          <div>
            <div className="text-xs font-bold text-gray-700 mb-3">Session Title</div>
            <div className="space-y-3">
              {sessionSlots.map(i=>(
                <div key={i} className="space-y-2">
                  <div className="text-xs text-gray-500 font-semibold">Session {i+1}</div>
                  <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="English" value={sessionTitles[i]?.en??''}
                      onChange={e=>setSessionTitles(prev=>({...prev,[i]:{...(prev[i]??{en:'',ar:'',ur:''}),en:e.target.value}}))}/>
                    <Input placeholder="Arabic" dir="rtl" value={sessionTitles[i]?.ar??''}
                      onChange={e=>setSessionTitles(prev=>({...prev,[i]:{...(prev[i]??{en:'',ar:'',ur:''}),ar:e.target.value}}))}/>
                    <Input placeholder="Urdu" dir="rtl" value={sessionTitles[i]?.ur??''}
                      onChange={e=>setSessionTitles(prev=>({...prev,[i]:{...(prev[i]??{en:'',ar:'',ur:''}),ur:e.target.value}}))}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Package Settings ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Package Settings"/>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Publish */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={publish} onChange={()=>setPublish(v=>!v)} id="pub"/>
            <span className="text-sm text-gray-700">Publish</span>
          </div>
          {/* Pickup */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={pickup} onChange={()=>setPickup(v=>!v)} id="pck"/>
            <span className="text-sm text-gray-700">Pickup Allowed</span>
          </div>
          {/* Certificate */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={certificate} onChange={()=>setCertificate(v=>!v)} id="cert"/>
            <span className="text-sm text-gray-700">Certificate Fee Included</span>
          </div>
          {/* Cash */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={cashPay} onChange={()=>setCashPay(v=>!v)} id="cash"/>
            <span className="text-sm text-gray-700">Pay By Cash</span>
          </div>
          {/* Change Trainer */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={trainerChange} onChange={()=>setTrainerChange(v=>!v)} id="tc"/>
            <span className="text-sm text-gray-700">Change Trainer Allowed</span>
          </div>
          {/* Reschedule */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
            <Toggle checked={reschedule} onChange={()=>setReschedule(v=>!v)} id="res"/>
            <span className="text-sm text-gray-700">Reschedule Allowed</span>
          </div>
          {/* Free pickup count (conditional) */}
          {pickup && (
            <div>
              <Label>Free Pickup Count</Label>
              <Input type="number" min="0" value={freePickup} onChange={e=>setFreePickup(e.target.value)} placeholder="0"/>
            </div>
          )}
          {/* Free trainer change count (conditional) */}
          {trainerChange && (
            <div>
              <Label>Free Trainer Change Count</Label>
              <Input type="number" min="0" value={freeTrainerChange} onChange={e=>setFreeTrainerChange(e.target.value)} placeholder="0"/>
            </div>
          )}
          {/* Reschedule fields (conditional) */}
          {reschedule && (
            <>
              <div>
                <Label>Free Reschedule Count</Label>
                <Input type="number" min="0" value={freeReschedule} onChange={e=>setFreeReschedule(e.target.value)} placeholder="0"/>
              </div>
              <div>
                <Label>Reschedule Min Before</Label>
                <div className="relative">
                  <Input type="number" value={rescheduleMin} onChange={e=>setRescheduleMin(e.target.value)} className="pr-16"/>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Minute</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Distance Settings ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <SectionHeader title="Distance Settings"/>
        <div className="max-w-xs">
          <Label>Free Kilometer</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">🛣️</span>
            <Input value={freeKm} onChange={e=>setFreeKm(e.target.value)} type="number" min="0" step="0.1" placeholder="0" className="pl-8 pr-12"/>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-semibold">KM</span>
          </div>
        </div>
      </div>

      {/* ── Bottom actions ── */}
      <div className="flex justify-end gap-3">
        <button onClick={()=>navigate('/dashboard/package/list')}
          className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={handleSave} disabled={loading}
          className="px-8 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold transition-colors disabled:opacity-50">
          {loading ? 'Saving…' : isEdit ? 'Update' : 'Save'}
        </button>
      </div>
    </div>
  )
}
